// ════════════════════════════════════════════════════════════
//  Circuit.jsx — THE LEAGUE: the stage, the standings, the Meta, the
//  Wire, the Hall, the week — plus the keeper that runs everywhere
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "I want someone to be able to tune and watch some
//  important fights always chasing after for something meaningful, real
//  rankings real standings etc". One stage, one fight at a time; every
//  league bout moves the table; the table IS the ranking.
//  Plain DOM, same look as the other online screens. Every number comes
//  from online/circuit.js (rules) and online/stage|keeper|ledger.js.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from "react";
import { LINES, portraitUrl } from "../data/roster.js";
import { fbNet as net } from "../online/net.js";
import { serverNow } from "../online/ie.js";
import { BetPanel, CALLED_BY } from "./Online.jsx";
import ChallengerStrip from "./ChallengerStrip.jsx";
import { BOX } from "../online/skybox.js";
import { loadStage, loadHour, hourOf, hourStart } from "../online/stage.js";
import { keep, record } from "../online/keeper.js";
import { wireOf, CHAMP_KINDS } from "../online/wire.js";
import {
  hourInfo, racingDayOf, statOf, rankKeys, BEINGS, finalR, hoursOfDay, DROP, CUP_SIZE, SEASON_WEEKS, avgWin, profileOf, boutLabel, finaleAt, REC, cupPlan, apocalypsePlan, GENESIS_MS, willieLine, RING, RING_BIG, ringOf, lockAtOf, LOCK_GRACE_MS,
  closerIndex, zonesOf, stakesOf, spotOf, spotTitle, agenda,
  CUPS, cupsOfDay, cupHours, roundName, isChKey, nameOfKey, QUALIFY, V2_MS, kindHours, dayHonours, weekHonours, weekOf,
  laneOf, callOf, divRec, LANES, NIGHT_ALL, QUALS, salvationField, isStandout, isCrowned, crownTier, dayIsV3, CHALLENGERS, dowOfDay, V3_MS, RING_K,
} from "../online/circuit.js";
import "./league3.css";
import BeingCard from "./BeingCard.jsx";
import Bracket from "./Bracket.jsx";
import { beingCard } from "../online/cards.js";
import { useCircuit, setCircuit, refreshCircuit, useFavs, toggleFav, openBeing, useOpenBeing } from "./circuitStore.js";
import { matchResult, replayMatch } from "../online/lockstep.js";
import { cursorOf, leagueOf, hallOf } from "../online/ledger.js";
import { metaBoard, mindView } from "../online/mind.js";
import { STYLES } from "../sim/battle.js";
import { Crown, CrownTag, RankPill } from "./Crown.jsx";

// the console handle for checking the League live (like window.__clash)
if (typeof window !== "undefined") window.__circuit = { net, loadStage, loadHour, keep, matchResult, replayMatch, cursorOf, leagueOf, serverNow };

const clock = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const localTime = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const inTxt = (ms) => { const m = Math.max(1, Math.round(ms / 60000)); return m < 60 ? `${m}m` : m < 2880 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.round(m / 1440)} days`; };
const untilTxt = (ms) => { const m = Math.max(1, Math.round(ms / 60000)); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };
const mmss = (s) => (s >= 1e8 ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);
const useTick = (ms = 1000) => { const [, set] = useState(0); useEffect(() => { const id = setInterval(() => set((n) => n + 1), ms); return () => clearInterval(id); }, [ms]); };

const Trio = ({ team, tier = "beb" }) => <span className="hb-trio">{(team || []).map((id, i) => LINES[id] ? <i key={i} title={LINES[id].lord} style={{ backgroundImage: `url(${portraitUrl(id, tier)})` }} /> : null)}</span>;

// ── words ────────────────────────────────────────────────────
const WHY = { ruined: "ruined every being", called: "called at the bell", forfeit: "a no-show — forfeit", paper: "fought while nobody watched" };
const whyOf = (f) => (f.why === "called" && f.by ? CALLED_BY[f.by] : f.why === "forfeit" && f.by === "coin" ? "both no-shows — the coin decided" : WHY[f.why] || f.why);
const KIND_CLASS = { main: "k-peak", winners: "k-prime", finale: "k-retribution", losers: "k-apocalypse", apocalypse: "k-apocalypse", prime: "k-prime",
  qual: "k-prime", rounds: "k-prime", bbcup: "k-prime", kccup: "k-prime", lccup: "k-apocalypse", ttcup: "k-apocalypse", ccup: "k-peak", salvation: "k-peak" };

const CUP_TAG = { q1: "Circuit Qualifier I", q2: "Contenders Qualifier I", q3: "Circuit Qualifier II", q4: "Contenders Qualifier II", k: "Contenders Cup", c: "Circuit Cup", x: "Champions Cup", l: "Losers' Cup", w: "Winners' Cup", nc: "Night Crown", bb: "Bottom vs Bottom", kc: "Contenders Cup", lc: "Last Chance", tt: "Top vs Top", s: "Salvation" };
/** WHAT KIND of bout — one word, one colour, everywhere a bout shows (hero chip, row tag, Up-next filter). */
const KINDS = [["c", "Circuit"], ["k", "Contenders"], ["big", "Big fights"]];
function kindOf(f) {
  if (f.k === "apo") return { key: "big", tag: "Apocalypse" };
  if (f.k === "cup") return { key: "big", tag: f.c === "f" ? "Finale" : CUP_TAG[f.c] || "Cup" };
  if (f.fx === "main") return { key: "big", tag: "Main Event" };
  return f.dv === "k" ? { key: "k", tag: "Contenders" } : { key: "c", tag: "Circuit" };
}
const closeBeing = () => openBeing(null);   // stable, so the page's Escape listener isn't re-made every tick
/** Who a key is, for the boards. */
// (a Challenger — a player or partner — shows the three they last fought with, so their face is on every board like anyone's)
const whoIs = (key) => BEINGS.get(key) || { key, name: nameOfKey(key), team: (CHALLENGERS.get(key) && CHALLENGERS.get(key).t) || [], home: isChKey(key) ? "challenger" : "gone" };
/** The three they fight with NOW (they change — online/mind.js), falling back to their cast team. */
const teamNow = (C, key) => (C && C.minds && C.minds.n[key] && C.minds.n[key].t.length ? C.minds.n[key].t : whoIs(key).team);
/** A Challenger's little badge (a player or partner on the League's tables). */
const ChTag = ({ k }) => (isChKey(k) ? <em className="ch-tag" title="A Challenger — a player or partner in the League">Challenger</em> : null);
const nameOfId = (id) => (LINES[id] ? LINES[id].name : STYLES[id] ? STYLES[id].name : id);
const pct = (x) => `${Math.round(100 * (x || 0))}%`;
const recOf = (s, rec = REC.week) => { const [w, l] = rec(s); return `${w}–${l}`; };
/** Which record a division's WEEK table reads: V3 counts only the bouts fought in that division (people change division mid-week). */
const weekRec = (C, div) => (C && C.v3 ? divRec(div === "circuit" ? "c" : "k") : REC.week);
/** A being's place on its division's table this week: { div:"circuit"|"contenders", pos } or null. */
function placeOf(C, key) {
  if (!C || !C.league) return null;
  const div = C.league.circuit.includes(key) ? "circuit" : C.league.contenders.includes(key) ? "contenders" : null;
  if (!div || !C.league[div].some((k) => { const s = statOf(C.S, k); return s.lw + s.ll > 0; })) return null;   // 0–0 all round (preseason, a week's first minutes): no places yet
  return { div, pos: rankKeys(C.league[div], C.S, weekRec(C, div)).indexOf(key) + 1 };
}

// ════════════════════════════════════════════════════════════
//  THE KEEPER — every signed-in screen fights the fights nobody watched
// ════════════════════════════════════════════════════════════
export function useCircuitKeeper(on, quiet = false) {
  const busy = useRef(false);
  useEffect(() => {
    // mid-fight it rests entirely: a replay is ~0.2 s of main thread and a live fight must never stutter
    if (!on.authUid || quiet) return undefined;
    const tick = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const r = await keep(net, serverNow(), { budgetMs: 1200 });
        setCircuit({ behind: r.behind, keeping: r.behind > 0 });
        if (r.behind === 0) refreshCircuit();
      } catch (e) { console.warn("[league keeper]", e); } // eslint-disable-line no-console
      busy.current = false;
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, [on.authUid, quiet]);
}

// ════════════════════════════════════════════════════════════
//  ONE BOUT
// ════════════════════════════════════════════════════════════
// A bet fold (with the Odds inside the bet panel). The pool is per PAIRING.
function BetFold({ on, f }) {
  const [open, setOpen] = useState(false);
  if (f.open) return <span className="hb-why" title="A player may still take this seat: who fights is settled at the top of the hour">Seat open · bets at the hour</span>;
  return (
    <details className="on-bet-fold" onToggle={(e) => setOpen(e.currentTarget.open)}><summary>Bet · odds</summary>
      {open && <BetPanel on={on} path={f.path} pool={`${f.path}/bets_${f.A.key}_${f.B.key}`} names={[f.A.name, f.B.name]} lockAt={f.startAt ?? null}
        keys={[f.A.key, f.B.key]} fighters={[f.A.uid, f.B.uid].filter(Boolean)} />}
    </details>
  );
}
/** The spotlight follows the pointer across the card (CSS variables only — never a re-render). */
const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
  e.currentTarget.style.setProperty("--my", `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
};
/** Willie's guess as one tug-of-war bar (from the ratings; a guess, never a promise). */
function Tug({ f, C }) {
  const p = willieLine(statOf(C.S, f.A.key), statOf(C.S, f.B.key)).p, a = Math.round(p[0] * 100);
  return (
    <div className="fo-tug" title="Willie's guess, from the ratings — never a promise">
      <em>{a}%</em><span><i style={{ width: `${a}%` }} /></span><em>{100 - a}%</em>
    </div>
  );
}
/** The motes of a spotlight bout drifting up through the card (look only: a few for ★, more for ★★★). */
const MOTES = Array.from({ length: 14 }, (_, i) => ({ x: `${((i * 53 + 9) % 94) + 3}%`, d: `${6 + ((i * 5) % 6)}s`, w: `${-(i * 1.9)}s`, s: `${3 + (i % 3)}px` }));
const MOTES_BY_LEVEL = [0, 4, 9, 14];
const Motes = ({ level }) => <span className="fo-motes" aria-hidden>{MOTES.slice(0, MOTES_BY_LEVEL[level] || 0).map((m, i) => <i key={i} style={{ "--x": m.x, "--d": m.d, "--w": m.w, "--s": m.s }} />)}</span>;
/** THE FACE-OFF — the bout on air (or at ringside), big. sp = its spotlight when it's the hour's closer, stakes = what's on the line. */
function FaceOff({ f, C, on, onWatch, onRingside, favs, sp = null, stakes = null }) {
  if (!f || !f.A || !f.B) return <div className="fo empty">The next card goes up at :50 — ten minutes before the hour.</div>;
  const now = serverNow(), feat = f.k !== "lb" || f.fx === "main", kd = kindOf(f);
  const total = (feat ? RING_BIG : f.k === "lb" && laneOf(f) === 1 ? RING_K : RING) * 1000, left = f.startAt ? f.startAt - now : null;
  const call = callOf(f), elapsed = f.phase === "live" && f.startAt ? Math.min(call, Math.max(0, (now - f.startAt) / 1000)) : 0;
  const p = f.phase === "live" ? elapsed / call : f.phase === "done" ? 1 : left != null ? 1 - Math.min(1, Math.max(0, left / total)) : 0;
  const urgent = f.phase === "prep" && left != null && left <= 10000;   // the last ten seconds before the bell
  const bellAt = f.startAt != null ? f.startAt : f.estAt, nearBell = f.phase === "prep" || (bellAt != null && bellAt - now <= SKY_MS);   // (2026-10-22) within SKY_MS of the bell you can wait in the Sky Box
  const side = (x, s) => {
    const pl = placeOf(C, x.key), won = f.winner === s, lost = f.winner != null && !won, fav = favs.has(x.key);
    return (
      <div className={"fo-side s" + s + (won ? " won" : "") + (lost ? " lost" : "")}>
        <Trio team={x.team} tier="lord" />
        {won && <span className="fo-win">Winner</span>}
        <div className="fo-nm">
          {/* (2026-10-22) a Challenger — a player or partner — is a fighter like any other: their card, and fans can follow them; their Mark one tap away */}
          {x.uid && !isChKey(x.key) ? <b className="being-link guest" title="A player, here as a guest" onClick={() => BOX.ui.openMark && BOX.ui.openMark(x.uid)}>{x.name}</b>
            : <b className="being-link" onClick={() => openBeing(x.key)}>{x.name}</b>}
          {(!x.uid || isChKey(x.key)) && <button className={"cc-fav" + (fav ? " on" : "")} title={fav ? "Stop following" : "Follow — starred on every board"} onClick={() => toggleFav(x.key)}>★</button>}
          {x.uid && isChKey(x.key) && BOX.ui.openMark && <button className="cc-fav" title="Their Mark" onClick={() => BOX.ui.openMark(x.uid)}>◉</button>}
        </div>
        {pl && <small>#{pl.pos} {pl.div === "circuit" ? "in the Circuit" : "of the Contenders"}</small>}
      </div>
    );
  };
  return (
    <div className={"fo " + f.phase + (feat ? " feature" : "") + " kd-" + kd.key + (sp ? " closer lv" + sp.level + (sp.night ? " night" : "") : "") + (urgent ? " urgent" : "")} style={{ "--p": p }} onMouseMove={spot}>
      {sp && <Motes level={sp.level} />}
      {sp && <div className="fo-rib"><div className="fo-rib-in"><b>{"★".repeat(sp.level)}</b><span>{sp.night ? `${sp.night} · ` : ""}{spotTitle(sp)}</span></div></div>}
      <div className="fo-what">{f.phase === "live" ? <><i className="fo-dot" />Live · {mmss(elapsed)}</> : f.phase === "prep" ? `Ringside · bell in ${clock(left)}` : f.phase === "done" ? "Final" : f.startAt ? `Bell ${localTime(f.startAt)}` : f.estAt ? `Bell ${f.est ? "about " : ""}${localTime(f.estAt)}` : "Up next"}<span className="fo-sep"> · </span><span className={"fo-bout kd-chip kd-" + kd.key}>{boutLabel(f)}</span></div>
      <div className="fo-ring">
        {side(f.A, 0)}
        <div className="fo-vs">
          <svg viewBox="0 0 100 100" aria-hidden><circle className="trk" cx="50" cy="50" r="44" /><circle className="prog" cx="50" cy="50" r="44" pathLength="100" /></svg>
          <b>{f.phase === "prep" && left != null ? clock(left) : "VS"}</b>
        </div>
        {side(f.B, 1)}
      </div>
      <Tug f={f} C={C} />
      {stakes && <div className={"fo-stake z-" + stakes.zone}><i />{stakes.text}</div>}
      <div className="fo-act">
        {f.phase === "live" && <button className="btn primary big" onClick={() => onWatch(f.id)}>Watch it live</button>}
        {(f.phase === "soon" || f.phase === "prep") && onRingside && <button className="btn primary big" onClick={() => onRingside(f.id)}>Go ringside</button>}
        {(f.phase === "soon" || f.phase === "prep") && onRingside && nearBell && <button className="btn big ghost" title="Wait for the bell in your Sky Box" onClick={() => { BOX.wantView = "sky"; onRingside(f.id); }}>Wait in the Sky Box</button>}
        {(f.phase === "soon" || f.phase === "prep") && <BetFold on={on} f={f} />}
        {f.phase === "done" && <span className="hb-why">{whyOf(f)}</span>}
      </div>
      {f.phase === "live" && <i className="fo-clock" />}
    </div>
  );
}
/** ONE LINE per bout — tap it for Ringside and the bet. sp = the hour's closer (★), gap = it sits further down the card than the list reaches. */
function BoutRow({ f, on, onRingside, favs, i = 0, sp = null, gap = false }) {
  const [open, setOpen] = useState(false);
  if (!f || !f.A || !f.B) return null;
  const feat = f.k !== "lb" || f.fx === "main", done = f.phase === "done", kd = kindOf(f);
  const when = done || f.startAt ? localTime(f.startAt) : f.estAt ? `${f.est ? "~" : ""}${localTime(f.estAt)}` : f.late ? "if time" : "";   // a bell not known yet is a guess from the fight before (~)
  const fresh = done && f.why !== "paper" && f.endAt != null && serverNow() - f.endAt < 25000;   // it just finished: the row glows once
  const what = feat && kd.tag !== "Main Event" ? boutLabel(f).replace(/^the /, "")
    : sp && sp.level > 1 ? [sp.night, sp.lastNight ? "last bout of the night" : null, sp.stakes && sp.stakes.text].filter(Boolean).join(" · ") : "";
  const nm = (x, s) => <span className={"br-name" + (done ? (f.winner === s ? " won" : " lost") : "") + (favs.has(x.key) ? " fav" : "")}>{x.name}</span>;
  const canAct = !done && (f.phase === "soon" || f.phase === "prep"), canOpen = canAct || done;
  return (
    <div className={"br " + f.phase + (feat ? " feature" : "") + (sp ? " closer lv" + sp.level : "") + (gap ? " far" : "") + (fresh ? " fresh" : "") + (open ? " open" : "")} style={{ "--i": i }}>
      <button className="br-line" onClick={() => canOpen && setOpen(!open)} disabled={!canOpen} title={done ? "How it ended" : canAct ? "Ringside and bets" : ""}>
        <span className="br-t"><span className="br-when">{when}</span><em className={"br-kd kd-" + kd.key + (sp ? " closer" : "")}>{sp ? `${"★".repeat(sp.level)} Closer` : kd.tag}</em></span>
        <span className="br-a"><Trio team={f.A.team} />{nm(f.A, 0)}</span>
        <span className="br-vs">vs</span>
        <span className="br-b">{nm(f.B, 1)}<Trio team={f.B.team} /></span>
        {what && <span className="br-what">{what}</span>}
      </button>
      {open && done && <div className="br-more"><span className="hb-why">{whyOf(f)}{f.endAt && f.startAt && f.why !== "paper" ? ` · ${mmss((f.endAt - f.startAt) / 1000)}` : ""}</span></div>}
      {open && canAct && (
        <div className="br-more">
          {onRingside && <button className="btn tiny ghost" onClick={() => onRingside(f.id)}>Ringside</button>}
          <BetFold on={on} f={f} />
        </div>
      )}
    </div>
  );
}

// what's happening right now, in one plain line + the moment it's counting to
export function stageStatus(st, now) {
  if (!st || !st.cur) return { line: "Finding the stage…", at: null };
  if (st.cur.pending) return { line: "Willie's catching up on fights nobody watched…", at: null };
  if (st.on) return { line: `${st.on.A.name} vs ${st.on.B.name} — ${boutLabel(st.on)} is on`, at: null, live: true };
  const prep = st.up.find((f) => f.phase === "prep");
  if (prep) return { line: `Ringside: ${prep.A.name} vs ${prep.B.name} — bets close at the bell`, at: prep.startAt };
  const soon = st.up.find((f) => f.startAt && f.A && f.B);
  if (soon) return { line: `Next: ${soon.A.name} vs ${soon.B.name}`, at: soon.startAt };
  // the next card goes up at :50 — from then on it's the headline
  const nx = st.next, open = nx && nx.locked && now < nx.start ? nx.bouts.find((f) => f.A && f.B) : null;
  if (open) return { line: `The ${localTime(nx.start)} card is out — ${open.A.name} vs ${open.B.name} opens it`, at: nx.start };
  const nextH = hourOf(now) + 1;
  return { line: now < lockAtOf(nextH) ? "The hour's card is done — the next one goes up at :50" : "The hour's card is done — the next one is on its way", at: hourStart(nextH) };
}

// ════════════════════════════════════════════════════════════
//  TONIGHT — the evening's big thing, one line + one bubble
// ════════════════════════════════════════════════════════════
const ROUND_TITLE = { "round of 16": "Round of 16", quarterfinal: "Quarterfinals", semifinal: "Semifinals", final: "Final" };
function CupBracket({ C, c, day = null }) {
  // the season's rows on a season's last Saturday: the Finale seeds by the season, and the Losers' Cup leaves its eight out
  const P = cupPlan(c, C.week, { league: C.league, weekRows: C.rows, seasonRows: C.season ? C.season.rows : null, entered: C.entered || null }, day);
  const who = (k) => (k ? <b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b> : <em>—</em>);
  const won = (r, i) => P.winner(r, i);
  const size = CUPS[c] ? CUPS[c].size : 8;
  return (
    <div className={"tn-bracket" + (size === 16 ? " b16" : "")}>
      {Array.from({ length: P.rounds }, (_, r) => r).map((r) => (
        <div key={r} className="tn-round"><h5>{ROUND_TITLE[roundName(c, r)] || ""}</h5>
          {Array.from({ length: (size / 2) >> r }, (_, i) => { const b = P.bout(r, i), w = won(r, i); return (
            <div key={i} className={"tn-pair" + (w ? " done" : "")}>{b ? <>{who(b.a)}{w === b.a && " ✓"}<i>vs</i>{who(b.b)}{w === b.b && " ✓"}</> : <em>waiting on the round before</em>}</div>
          ); })}
        </div>
      ))}
      {P.champion && <div className="tn-champ">🏆 {who(P.champion)}</div>}
    </div>
  );
}
function Series({ C }) {
  const P = apocalypsePlan(C.league, C.rows, C.week);
  const who = (k) => <b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b>;
  return (
    <div className="tn-series">
      {P.safe && <p className="on-note">{who(P.safe)} won the Losers' Cup — safe.</p>}
      {P.series.map((s) => (
        <div key={s.i} className={"tn-pair" + (s.done ? " done" : "")}>{who(s.a)}<span className="tn-score">{s.wins[0]} – {s.wins[1]}</span>{who(s.b)}
          <em>{s.done ? (s.winner === s.b ? "up to the Circuit" : "seat saved") : "best of 3"}</em></div>
      ))}
    </div>
  );
}
/** V3: tonight's brackets in ONE bubble — a little switch picks the cup (the next final's, to start). */
function Brackets({ C, cups, day }) {
  const now = serverNow();
  const next = cups.find((c) => { const hs = cupHours(c, day); return hs.length && hourStart(hs[hs.length - 1]) + 3600e3 > now; }) || cups[cups.length - 1];
  const [c, setC] = useState(next);
  return (
    <div className="br3">
      <div className="seg br3-seg">{cups.map((x) => <button key={x} className={x === c ? "on" : ""} onClick={() => setC(x)}>{CUP_TAG[x] || "The Finale"}</button>)}</div>
      <p className="rt-sub">{CUP_LINE[c] || ""}</p>
      <CupBracket C={C} c={c} day={day} />
    </div>
  );
}
const QLINE = "A baby tournament: 8 fighters, 7 fights, back to back. Win it and you take a seat in tonight's cups. Later rounds are named the moment the fight before ends.";
const CUP_LINE = {
  q1: QLINE, q2: QLINE, q3: QLINE, q4: QLINE,
  bb: "The Circuit's bottom 8 vs the Contenders' bottom 8. No escape — just the win.",
  kc: "All sixteen Contenders.",
  lc: "The Contenders' top 8 vs the Circuit's bottom 8. A Contender who wins it escapes to the Circuit.",
  tt: "The top 8 of each. A Contender who wins it escapes to the Circuit.",
  c: "All sixteen of the Circuit. Its final opens the night's last hour — the Night Crown follows it.",
  nc: "One fight, after the Circuit Cup final: the night's two best champions from the earlier finals, picked by how the night went. Win it and you wear the Night Crown — the biggest crown of the night.",
  s: "Everyone over the points line, best first — the King's Cup.",
  l: "Bragging rights. Every player not in Salvation can check in.",
  f: "The season's top 8 of the Circuit. Win it and you're the champion.",
};
/** V3: this week's escapes, night by night (who got out, who fell). */
function Escapes({ C }) {
  const nights = (C.league && C.league.nights) || [];
  const who = (k) => <b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b>;
  if (!nights.length) return <p className="on-note">Nobody's escaped yet this week. Last Chance and Top vs Top, every night.</p>;
  return <div className="tn-series">{nights.map((n) => n.up.map((k, i) => (
    <div key={`${n.day}:${k}`} className="tn-pair done">{who(k)}<em>escaped</em>{n.down[i] && <>{who(n.down[i])}<em>fell</em></>}<span className="tn-score">{new Date(hourStart(hoursOfDay(n.day).slice(-1)[0])).toLocaleDateString([], { weekday: "short" })}</span></div>
  )))}</div>;
}
/** V3: THE RACE FOR SALVATION — the week's points, the line, who's over it. Short list; the rest is on Standings. */
function Race({ C, limit = 12 }) {
  if (!C.points || !C.points.size) return <p className="on-note">No points yet this week. Every win counts — Saturday decides.</p>;
  const all = [...C.points.values()].sort((a, b) => b.score - a.score);
  const inS = new Set(salvationField(C.points, () => true, C.line, C.standout));
  const shown = all.slice(0, limit);
  const lineAt = shown.findIndex((v) => v.score < C.line);
  return (
    <div className="race">
      <p className="rt-sub">The line is <b>{Math.round(C.line).toLocaleString()}</b> — the Circuit's middle score this week. Over it when Saturday's card goes up (6pm) and you're in Salvation, sixteen at most.{C.standout && C.standout.kp > 0 ? <> A Contender gets in on merit too: <b>{C.standout.kp.toLocaleString()}</b> Contender points.</> : null} <b>Win the Circuit Cup or the Night Crown</b> this week and your crown is a seat too.</p>
      {shown.map((v, i) => (
        <div key={v.key}>
          {i === lineAt && <div className="race-line"><span>the line</span></div>}
          <div className={"race-r" + (inS.has(v.key) ? " in" : "")} onClick={() => openBeing(v.key)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") openBeing(v.key); }}>
            <span className="race-n">{i + 1}</span><Trio team={teamNow(C, v.key)} /><b>{whoIs(v.key).name}</b><ChTag k={v.key} /><CrownTag C={C} k={v.key} />
            <em className="race-s">{Math.round(v.score).toLocaleString()}</em>
            <small>{C.league && C.league.circuit.includes(v.key) ? "Circuit" : "Contenders"}{isStandout(C.points, C.standout, v.key) ? " · a standout — in on merit" : ""}{isCrowned(C.points, v.key) ? " · crowned — in on merit" : ""}{v.paced ? ` · at their pace ×${v.paced}` : ""}</small>
          </div>
        </div>
      ))}
    </div>
  );
}
function Tonight({ C, go = null }) {
  const [open, setOpen] = useState(null);
  const now = serverNow(), today = racingDayOf(now), hours = hoursOfDay(today);
  if (!C.league || now < GENESIS_MS) return null;   // preseason: the on-air strip already says so
  const at = (h) => localTime(hourStart(h));
  const bubbles = [];   // [{ id, label, body }]
  let line;
  const allCups = cupsOfDay(today), cups = allCups.filter((c) => !QUALS.includes(c));   // (cups = the night's cups; the Qualifiers run every day)
  const apo = hours.filter((h) => hourInfo(h).kind === "apocalypse");
  const v3 = dayIsV3(today), dow = dowOfDay(today);
  const qLine = v3 && allCups.length > cups.length && dow > 4 ? <>The Qualifiers run from 11am — their points feed the climb to the weekend. </> : null;
  if (v3 && cups.length && dow <= 4) {
    // V3: THE CUP NIGHT — one final an hour from 6pm, least epic first; the brackets one tap away
    const fin = (c) => { const hs = cupHours(c, today); return hs[hs.length - 1]; };
    line = <>The Qualifiers run from 11am — win one and you're in tonight's cups. The cup night — finals {NIGHT_ALL.map((c, n) => <span key={c}>{n ? " · " : ""}{new Date(hourStart(fin(c))).toLocaleTimeString([], { hour: "numeric" })} <b>{CUP_TAG[c]}</b></span>)}. Two ways out for a Contender: Last Chance and Top vs Top. Every cup won leaves a crown.</>;
    bubbles.push({ id: "brackets", label: "Brackets", body: <Brackets C={C} cups={[...QUALS, ...NIGHT_ALL]} day={today} /> });
  } else if (v3 && cups.length) {
    // V3 Saturday: the Losers' Cup, then SALVATION (or the Finale)
    const fh = (c) => cupHours(c, today);
    line = <>{cups.includes("f") && <><b>The Season Finale</b> — from {at(fh("f")[0])}, the season's last fight at {at(fh("f").slice(-1)[0])}. </>}<b>Salvation</b> — from {at(fh("s")[0])}, final {at(fh("s").slice(-1)[0])}: everyone over the points line. The <b>Losers' Cup</b> from {at(fh("l")[0])}.</>;
    bubbles.push({ id: "brackets", label: "Brackets", body: <Brackets C={C} cups={allCups} day={today} /> });
  } else if (cups.length) {
    // V2: tonight's cups, each with its own bracket one tap away
    const parts = cups.map((c) => { const hs = cupHours(c, today); return { c, start: hs[0], fin: hs[hs.length - 1] }; }).sort((a, b) => a.start - b.start);
    line = <>{parts.map((p, n) => <span key={p.c}>{n ? " · " : ""}<b>{CUPS[p.c].name.replace(/^the /, "The ")}</b> {at(p.start)}, final {at(p.fin)}</span>)}.</>;
    for (const p of parts) bubbles.push({ id: p.c, label: CUP_TAG[p.c] || "The Finale", body: <CupBracket C={C} c={p.c} day={today} /> });
  } else if (apo.length) {
    line = <><b>Apocalypse</b> — from {at(apo[0])}. The Circuit's bottom {DROP} vs the Contenders' top {DROP}, best of 3, the seat on the line.</>;
    bubbles.push({ id: "apo", label: "The series", body: <Series C={C} /> });
    if (allCups.length) bubbles.push({ id: "brackets", label: "Qualifiers", body: <Brackets C={C} cups={allCups} day={today} /> });
  } else {
    // before V2: Thursday's / Saturday's cup, or the Main Event
    const kindOf = (k) => hours.find((h) => hourInfo(h).kind === k);
    const c = kindOf("losers") ? "l" : kindOf("winners") ? "w" : kindOf("finale") ? "f" : null;
    if (c) {
      const h = kindOf({ l: "losers", w: "winners", f: "finale" }[c]);
      line = <><b>{hourInfo(h).name}</b> — from {at(h)}. {c === "l" ? "The Circuit's bottom 8: win it and you're safe from Friday's drop." : c === "w" ? "The Circuit's top 8 this week. Final at 9pm." : "The season's top 8. Win it and you're the champion."}</>;
      bubbles.push({ id: c, label: "The bracket", body: <CupBracket C={C} c={c} /> });
    } else if (kindOf("main")) {
      const [a, b] = rankKeys(C.league.circuit, C.S);
      line = <><b>The Main Event</b> — 9pm. The Circuit's #1 vs #2{a && b ? <> — right now that's {whoIs(a).name} and {whoIs(b).name}</> : null}.</>;
    } else {
      line = <>League fights all day — the table decides who's in this week's cups.</>;
    }
  }
  // V3: the week's escapes and the race for Salvation — every day, one tap away
  if (v3) {
    if (C.league && (C.league.nights || []).length) bubbles.push({ id: "esc", label: `Escapes · ${C.league.escaped.length}`, body: <Escapes C={C} /> });
    if (dow !== 6 || hourOf(now) < (cupHours("l", today)[0] ?? Infinity)) bubbles.push({ id: "race", label: "The race for Salvation", body: <Race C={C} /> });
  }
  const cur = bubbles.find((b) => b.id === open);
  return (
    <div className="tn">
      <span>Tonight: {qLine}{line}</span>
      {bubbles.map((b) => <button key={b.id} className={"mt-bubble" + (open === b.id ? " on" : "")} onClick={() => setOpen(open === b.id ? null : b.id)}>{b.label}</button>)}
      {cur && cur.body}
      {cur && go && cur.id !== "esc" && cur.id !== "race" && <button className="btn ghost tn-full" onClick={() => go("bracket")}>The full bracket →</button>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  ON NOW — the stage
// ════════════════════════════════════════════════════════════
/** THE NEXT CARD — up at :50, ten minutes before its hour, so there's time to look it over and bet (caP'n 2026-10-17). */
function NextCard({ st, sps, on, onRingside, favs }) {
  const [open, setOpen] = useState(false);
  const nx = st && st.next;
  if (!nx || !nx.locked || serverNow() >= nx.start) return null;
  const list = nx.bouts.filter((f) => f.A && f.B && f.phase !== "scratched");
  if (!list.length) return null;
  const o = list[0], first = o.estAt || nx.start + LOCK_GRACE_MS + ringOf(nx.bouts, 0) * 1000;
  return (
    <div className="tn nc">
      <span><b>The {localTime(nx.start)} card is out</b> — {o.A.name} vs {o.B.name} opens it, first bell {localTime(first)}. Bets are open.</span>
      <button className={"mt-bubble" + (open ? " on" : "")} onClick={() => setOpen(!open)}>The card</button>
      {open && <div className="nc-list">{list.map((f, i) => <BoutRow key={f.id} i={i} f={f} on={on} onRingside={onRingside} favs={favs} sp={sps.get(f.id)} />)}</div>}
    </div>
  );
}
/**
 * WHICH BOUTS GET A SPOTLIGHT — each card's closer (its last Circuit bout that's sure to be fought), and what's on the line in a
 * Circuit bout as the table stands now (display only: circuit.js closerIndex / zonesOf / spotOf). Reads { sp: Map(id → spot), stakeOf(f) }.
 */
function spotsOf(st, C, safe) {
  const sp = new Map(), zs = new Map();
  const zonesAt = (h) => { if (!zs.has(h)) zs.set(h, C.league && C.S ? zonesOf(C.league, C.S, h, safe) : new Map()); return zs.get(h); };
  if (st) for (const H of [st.prev, st.cur, st.next]) {
    if (!H || !H.bouts.length || (H === st.next && !H.locked)) continue;
    const j = closerIndex(H.bouts, (f) => f.phase === "scratched" || f.late || !f.A || !f.B);
    if (j >= 0) sp.set(H.bouts[j].id, spotOf(H.bouts[j], H.info, zonesAt(H.hour), H.bouts[j].phase === "done"));
  }
  return { sp, stakeOf: (f) => (f && f.k === "lb" && f.dv === "c" && f.fx !== "main" && f.phase !== "done" ? stakesOf(f, zonesAt(f.h)) : null) };
}
/** "about 45" for a guess, the plain number when it's small. */
const aboutN = (n) => (n < 5 ? <b>{n}</b> : <>about <b>{(Math.round(n / 5) * 5).toLocaleString()}</b></>);
const dayTime = (ms) => new Date(ms).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
/**
 * WHAT'S LEFT — one quiet line under the face-off (how much of the hour, and of the week's big nights, is still to come), and
 * one bubble for the rest. Nothing here asks for attention; it's the "how much more is there?" you glance at between fights.
 */
function Pulse({ st, sps, star = null }) {
  const [open, setOpen] = useState(false);
  const now = serverNow(), minute = Math.floor(now / 60e3);
  const A = useMemo(() => agenda(now), [minute]); // eslint-disable-line react-hooks/exhaustive-deps
  const H = st && st.cur && !st.cur.pending ? st.cur : null;
  const left = H ? H.bouts.filter((f) => f.A && f.B && ["waiting", "soon", "prep", "live"].includes(f.phase)) : [];
  const firm = left.filter((f) => !f.late), iffy = left.length - firm.length;
  const n = (key) => firm.filter((f) => kindOf(f).key === key).length;
  const closer = H && H.bouts.find((f) => sps.has(f.id) && f.phase !== "done" && f.phase !== "scratched");
  const closeAt = closer && closer !== star && closer.phase !== "live" ? (closer.startAt != null ? closer.startAt : closer.estAt) : null;
  const evLeft = A.week.events.filter((e) => e.state !== "done");
  const next = A.tonight.next;
  return (
    <div className="pl">
      {firm.length > 0 && <span><b>{firm.length}</b> fight{firm.length === 1 ? "" : "s"} left this hour</span>}
      {closeAt != null && <span>last Circuit bout <b>{closer.startAt == null && closer.est ? "~" : ""}{localTime(closeAt)}</b></span>}
      {A.pre ? <span>Season 1 starts in <b>{inTxt(A.startsIn)}</b></span> : evLeft.length > 0 && <span><b>{evLeft.length}</b> big night{evLeft.length === 1 ? "" : "s"} left this week</span>}
      <button className={"mt-bubble" + (open ? " on" : "")} onClick={() => setOpen(!open)}>What's left</button>
      {open && (
        <div className="pl-more">
          <div className="pl-c"><h5>This hour</h5>
            {firm.length ? <p><b>{n("c")}</b> Circuit · <b>{n("k")}</b> Contenders{n("big") > 0 && <> · <b>{n("big")}</b> big</>}{iffy > 0 && <i> (+{iffy} if there's time)</i>}</p> : <p>This card's done.</p>}
            {closeAt != null && <p>Last Circuit bout <i>{closer.startAt == null && closer.est ? "about " : ""}{localTime(closeAt)}</i></p>}
          </div>
          <div className="pl-c"><h5>Tonight</h5>
            <p>{aboutN(A.tonight.fights)} more fights after this hour</p>
            {next && <p>{next.name} <i>{next.at <= now ? "on now" : `${localTime(next.at)} · in ${inTxt(next.at - now)}`}</i></p>}
          </div>
          <div className="pl-c"><h5>This week</h5>
            {A.pre ? <p>Season 1 begins <i>{dayTime(GENESIS_MS)}</i></p>
              : <>{A.week.events.map((e) => <p key={e.kind} className={"pl-ev " + e.state}>{e.state === "done" ? "✓ " : e.state === "on" ? "● " : ""}{e.name} <i>{dayTime(e.start)}</i></p>)}
                {A.week.mains > 0 && <p>{A.week.mains} Main Event{A.week.mains === 1 ? "" : "s"} to go</p>}</>}
            <p>{aboutN(A.week.fights)} fights to go</p>
          </div>
          <div className="pl-c"><h5>The season</h5>
            {A.season ? <><p>Week <b>{A.season.week}</b> of {A.season.weeks}</p><p>The Finale <i>{new Date(A.season.finale).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</i></p></>
              : <p>Starts when Season 1 does.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
/**
 * THE TWO STAGES (V3, caP'n 2026-10-21: "they run in unison so if I ever don't want to watch the Circuit … should be able to
 * throw her straight in"). One switch above the face-off — the Circuit stage or the Contenders stage, remembered per browser;
 * a dot on a stage that has a fight on right now. Up next lists BOTH, by bell time (the tags say which is which).
 */
const bellOf = (f) => (f.startAt != null ? f.startAt : f.estAt != null ? f.estAt : Infinity);
/**
 * HOW IMPORTANT A BOUT IS (caP'n 2026-10-22: "always put the most 'important' one front and center with the lesser fights like the
 * contenders just a click away"): the Season Finale > Salvation > the Circuit Cup > Top vs Top > Apocalypse > Last Chance > the
 * Contenders Cup > Bottom vs Bottom > the Losers' Cup; a final over a semi over earlier rounds; then a Circuit bout over a Contenders one.
 */
const CUP_WEIGHT = { q1: 2.6, q2: 2.4, q3: 2.2, q4: 2.0, f: 9, s: 8, x: 8, w: 8, nc: 7.5, c: 7, tt: 6, lc: 5, kc: 4, k: 4, bb: 3, l: 2 };
export function importance(f) {
  if (!f) return -1;
  if (f.k === "cup") return 100 * (CUP_WEIGHT[f.c] || 1) + (f.r === finalR(f.c) ? 50 : 10 * (f.r || 0));
  if (f.k === "apo") return 560 + 10 * (f.g || 0);
  if (f.fx === "main") return 300;
  return laneOf(f) === 0 || f.dv === "c" ? 20 : 10;
}
const SKY_MS = 15 * 60000;   // ⟵ DIAL — a fight opens on the island bar (and the Sky Box) this long before its bell
/** A name as the island bar wears it: the epithet comes off ("Kae'lith the Unshattered" → "Kae'lith") so nobody gets chopped; the full name stays on the hover. */
const nick = (n) => String(n).replace(/ the .+$/i, "");
/** 3 → "3rd" (for the rank pill's hover). */
const ordOf = (n) => { const t = n % 100, u = n % 10; return n + (t >= 11 && t <= 13 ? "th" : u === 1 ? "st" : u === 2 ? "nd" : u === 3 ? "rd" : "th"); };
/** Everyone's place on their division's table this week, ranked ONCE per draw (the island bar asks for ten of them): key → { div, pos }.
 *  Nobody in a division has fought yet → that division is left out (same rule as placeOf), so a fresh week's bar stays as plain as it was. */
function placesOf(C) {
  const out = new Map();
  if (!C || !C.league) return out;
  for (const div of ["circuit", "contenders"]) {
    const keys = C.league[div];
    if (!keys || !keys.some((k) => { const s = statOf(C.S, k); return s.lw + s.ll > 0; })) continue;
    rankKeys(keys, C.S, weekRec(C, div)).forEach((k, i) => out.set(k, { div, pos: i + 1 }));
  }
  return out;
}
/** The Night Crown's title a fighter wears: "lord" (the Circuit was in that fight) · "champ" (two Contenders) · "" — same source as <CrownTag>. */
const islTitle = (C, k) => { const x = C && C.crowns && C.crowns.get(k); return x && x.night ? (x.night.lord ? "lord" : x.night.champ ? "champ" : "") : ""; };
const divWord = (d) => (d === "circuit" ? "the Circuit" : "the Contenders");
/**
 * THE ISLAND BAR (caP'n 2026-10-22: "a little island bar above the fight screen … click on the circuit, contenders, or any other
 * upcoming fights, greyed out if they're more than 15 minutes away, less than that and you can wait in the sky box"). The two stages
 * are always one tap; every other upcoming fight is a chip with its countdown — greyed until its bell is within SKY_MS.
 */
/** One fighter on a chip: the name, and under it their place on this week's table + any King's Crown (a Night Lord / Night's Champion says so). */
function IslandSide({ C, who, places, side }) {
  const p = places.get(who.key), t = islTitle(C, who.key);
  return (
    <span className={"isl-w " + side + (t === "lord" ? " lord" : "")}>
      <span className="isl-n" title={who.name}>{nick(who.name)}</span>
      <span className="isl-m">
        {p && <RankPill div={p.div} pos={p.pos} title={`${ordOf(p.pos)} in ${divWord(p.div)} this week`} />}
        <CrownTag C={C} k={who.key} />
      </span>
    </span>
  );
}
function IslandBar({ C, two, lane, onBy, pinnedLane, pinnedId, extras, onLane, onFight, onAuto }) {
  const now = serverNow();
  const places = placesOf(C);
  const meta = places.size > 0 || !!(C && C.crowns && C.crowns.size > 0);   // nobody ranked or crowned yet → no second line at all
  // "(3rd in the Circuit, Night Lord)" — the chip's hover says in words what its badges say in pictures
  const said = (w) => {
    const p = places.get(w.key), t = islTitle(C, w.key);
    const bits = [p ? `${ordOf(p.pos)} in ${divWord(p.div)}` : null, t === "lord" ? "Night Lord" : t === "champ" ? "Night's Champion" : null].filter(Boolean);
    return bits.length ? ` (${bits.join(", ")})` : "";
  };
  return (
    <div className={"isl" + (meta ? " meta" : "")} role="tablist" aria-label="Pick a fight">
      {two && LANES.map((l, i) => <button key={i} role="tab" aria-selected={!pinnedId && lane === i} className={"lane-b l" + i + (!pinnedId && lane === i ? " on" : "")} onClick={() => onLane(i)}>{onBy[i] && <i className="fo-dot" title="A fight is on" />}{l.replace(/^The /, "")}</button>)}
      {two && extras.length > 0 && <span className="isl-sep" aria-hidden />}
      {extras.map((f) => {
        const at = bellOf(f), ms = at - now, live = f.phase === "live", open = live || ms <= SKY_MS, mine = pinnedId === f.id;
        const eta = live ? "Live" : at === Infinity ? "Later" : ms < 60000 ? "Starting" : ms < 3600000 ? `in ${Math.ceil(ms / 60000)}m` : localTime(at);
        const kd = kindOf(f), big = f.k !== "lb" || f.fx === "main";
        const tag = big ? boutLabel(f).replace(/^the /i, "") : kd.tag;   // WHAT it is, in the chip's own words: Circuit · Contenders · a Cup round · Main Event · Apocalypse
        const lord = islTitle(C, f.A.key) === "lord" || islTitle(C, f.B.key) === "lord";   // a Night Lord in the fight: the chip catches a slow gold sheen
        return (
          <button key={f.id} role="tab" aria-selected={mine} disabled={!open} className={"isl-f ik-" + kd.key + (open ? " open" : " far") + (live ? " live" : "") + (mine ? " on" : "") + (lord ? " lord" : "")}
            title={`${tag} — ${f.A.name}${said(f.A)} v ${f.B.name}${said(f.B)}. ` + (open ? "Open it — wait in the Sky Box or go ringside" : `Bell ${at === Infinity ? "not set yet" : localTime(at)} — opens ${Math.round(SKY_MS / 60000)} min before`)}
            onClick={() => onFight(f)}>
            <span className="isl-tag">{tag}</span>
            <small>{live && <i className="fo-dot" />}{eta}</small>
            <span className="isl-vs"><IslandSide C={C} who={f.A} places={places} side="a" /><em>v</em><IslandSide C={C} who={f.B} places={places} side="b" /></span>
          </button>
        );
      })}
      {(pinnedLane != null || pinnedId) && <button className="lane-auto" title="Put the most important fight back in front" onClick={onAuto}>Auto</button>}
    </div>
  );
}
function Stage({ st, C, on, onWatch, onRingside, favs, go = null }) {
  const [kf, setKf] = useState(() => { try { return localStorage.getItem("shatterproof.stageKind") || "all"; } catch { return "all"; } });
  const pickKf = (v) => { setKf(v); try { localStorage.setItem("shatterproof.stageKind", v); } catch { /* fine */ } };
  // a stage you pick stays picked for the rest of the hour; then (or with "Auto") the most important fight takes the front again
  const [pin, setPin] = useState(null);   // { lane, until }
  const pickLane = (v) => setPin({ lane: v, until: hourStart(hourOf(serverNow()) + 1) });
  const pickFight = (f) => setPin({ id: f.id, until: Math.max(bellOf(f), serverNow()) + 20 * 60000 });   // a fight you open stays in front through its bell
  const safe = useMemo(() => (C.league ? apocalypsePlan(C.league, C.rows, C.week).safe : null), [C.league, C.rows, C.week]);   // the Losers' Cup winner isn't in the drop zone
  const spots = useMemo(() => spotsOf(st, C, safe), [st, C.league, C.S, safe]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st || !st.cur) return <div className="on-note">Finding the stage…</div>;
  const two = !!(st.cur.info && st.cur.info.v3) || st.cur.bouts.some((f) => laneOf(f) === 1);
  const ups = two ? st.up.slice().sort((a, b) => bellOf(a) - bellOf(b)) : st.up;
  const onBy = st.onBy || [st.on, null];
  const nowOn = (ln) => onBy[ln] || ups.find((f) => laneOf(f) === ln) || null;   // a stage's fight: the one on, else its next
  const pinned = pin && pin.lane != null && serverNow() < pin.until ? pin.lane : null;
  const pinnedF = pin && pin.id && serverNow() < pin.until ? [st.on, ...ups].find((f) => f && f.id === pin.id && f.A && f.B) || null : null;   // gone (done / scratched) → auto takes over
  const auto = (() => { const a = nowOn(0), b = nowOn(1), ia = importance(a), ib = importance(b); return ib > ia || (ib === ia && b && b.phase === "live" && !(a && a.phase === "live")) ? 1 : 0; })();
  const lane = pinned != null ? pinned : auto;
  const star = pinnedF || (two ? (nowOn(lane) || st.on || ups[0] || null) : (st.on || st.up[0] || null));
  const other = two && !pinnedF ? nowOn(1 - lane) : null;
  // the island bar's chips: every other fight coming (cups and big nights first, then the league's), soonest bell first
  const lanePicks = two ? [nowOn(0), nowOn(1)] : [];
  const extras = ups.filter((f) => f && f.A && f.B && !lanePicks.includes(f) && f.phase !== "done").sort((a, b) => ((a.k === "lb") - (b.k === "lb")) || bellOf(a) - bellOf(b)).slice(0, 5);
  const rest = ups.filter((f) => f !== star);
  const fit = (f) => kf === "all" || kindOf(f).key === kf;
  let coming = rest.filter(fit).slice(0, 6);
  // the hour's closer is always in sight: further down the card than the list reaches, it joins the end
  const far = rest.find((f) => spots.sp.has(f.id) && fit(f) && !coming.includes(f));
  if (far) coming = [...coming, far];
  const fought = [st.cur, st.prev].filter(Boolean).flatMap((H) => H.bouts.filter((f) => f.phase === "done").reverse()).slice(0, 6);
  // only the kinds that are actually coming (plus the one picked, so there's always a way back to All)
  const opts = [["all", "All"], ...KINDS.filter(([v, l]) => v === kf || rest.some((f) => kindOf(f).key === v))];
  return (
    <div className="stg">
      {(two || extras.length > 0) && <IslandBar C={C} two={two} lane={lane} onBy={onBy} pinnedLane={pinned} pinnedId={pinnedF ? pinnedF.id : null} extras={extras} onLane={pickLane} onFight={pickFight} onAuto={() => setPin(null)} />}
      <FaceOff key={star ? star.id : "none"} f={star} C={C} on={on} onWatch={onWatch} onRingside={onRingside} favs={favs} sp={star ? spots.sp.get(star.id) : null} stakes={star ? spots.stakeOf(star) : null} />
      {other && other.A && other.B && (
        <div className="also">
          {other.phase === "live" && <i className="fo-dot" />}
          <span>{LANES[1 - lane].replace(/^The /, "")}: <b>{other.A.name}</b> vs <b>{other.B.name}</b>{other.phase === "live" ? " — on now" : other.startAt ? ` · ${localTime(other.startAt)}` : other.estAt ? ` · ~${localTime(other.estAt)}` : ""}{other.k !== "lb" ? ` · ${boutLabel(other).replace(/^the /, "")}` : ""}</span>
          <button className="btn tiny ghost" onClick={() => pickLane(1 - lane)}>Switch</button>
        </div>
      )}
      <Pulse st={st} sps={spots.sp} star={star} />
      <NextCard st={st} sps={spots.sp} on={on} onRingside={onRingside} favs={favs} />
      <Tonight C={C} go={go} />
      <div className="stg-cols">
        <section>
          <div className="stg-hd"><h4>Up next</h4>{(opts.length > 2 || kf !== "all") && <div className="kd-f">{opts.map(([v, l]) => <button key={v} className={"kd-" + v + (kf === v ? " on" : "")} onClick={() => pickKf(v)}>{l}</button>)}</div>}</div>
          {coming.length ? coming.map((f, i) => <BoutRow key={f.id} i={i} f={f} on={on} onRingside={onRingside} favs={favs} sp={spots.sp.get(f.id)} gap={f === far} />) : <p className="on-note">{kf === "all" ? "The next card goes up at :50." : `No ${KINDS.find(([v]) => v === kf)[1].toLowerCase()} coming up yet.`}</p>}
        </section>
        <section><h4>Just fought</h4>{fought.length ? fought.map((f, i) => <BoutRow key={f.id} i={i} f={f} on={on} favs={favs} sp={spots.sp.get(f.id)} />) : <p className="on-note">Nothing yet this hour.</p>}</section>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  THE STANDINGS — the table IS the ranking
// ════════════════════════════════════════════════════════════
function nextFights(st) {
  const out = new Map();
  if (!st) return out;
  for (const f of [st.on, ...st.up]) if (f && f.A && f.B) for (const k of [f.A.key, f.B.key]) if (!out.has(k)) out.set(k, f);
  return out;
}
const streakOf = (s) => (s.streak > 0 ? `W${s.streak}` : s.streak < 0 ? `L${-s.streak}` : "—");
/** ▲▼ since today began: how many places a being moved (+ = up). Nothing before today → no arrows. */
const moved = (k, ranked, before0) => {
  if (!before0) return 0;
  const before = before0.indexOf(k), now = ranked.indexOf(k);
  return before < 0 || now < 0 ? 0 : before - now;
};
// THE RANK FLASH (caP'n: "watching the rankings change"): every table remembers where everyone stood
// the last time it was drawn — across tab switches too — and a row that just moved glows for a while
const FLASH_MS = 7000;
const RANK_MEMO = new Map();   // table → { pos: Map(key → place), flash: Map(key → { d, until }) }
function useRankFlash(ranked, table) {
  let T = RANK_MEMO.get(table);
  if (!T) { T = { pos: null, flash: new Map() }; RANK_MEMO.set(table, T); }
  const now = Date.now();
  const pos = new Map(ranked.map((k, i) => [k, i]));
  if (T.pos && T.pos.size) for (const [k, i] of pos) { const b = T.pos.get(k); if (b != null && b !== i) T.flash.set(k, { d: b - i, until: now + FLASH_MS }); }
  T.pos = pos;
  for (const [k, v] of T.flash) if (v.until <= now) T.flash.delete(k);
  return T.flash;
}
const nextTxt = (f) => (f.phase === "live" ? "on now" : f.startAt ? localTime(f.startAt) : f.estAt ? `${f.est ? "~" : ""}${localTime(f.estAt)}` : "soon");
function Standings({ C, st, favs }) {
  const ls = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
  const [view, setView] = useState(() => ls("shatterproof.standings", "week"));
  const [div, setDiv] = useState(() => ls("shatterproof.division", "circuit"));
  const keep = (k, v, set) => { set(v); try { localStorage.setItem(k, v); } catch { /* fine */ } };
  const seg = (k, opts, cur, set) => <div className="seg">{opts.map(([v, l]) => <button key={v} className={cur === v ? "on" : ""} onClick={() => keep(k, v, set)}>{l}</button>)}</div>;
  const nf = nextFights(st);
  const league = C.league;
  const race = view === "race" && C.v3, season = view === "season", circuit = div !== "contenders";
  const keys = league ? league[circuit ? "circuit" : "contenders"] : [];
  const rec = season ? (circuit ? REC.circuit : REC.contenders) : weekRec(C, circuit ? "circuit" : "contenders");
  const S = season && C.season ? C.season.S : C.S, S0 = season && C.season ? C.season.S0 : C.S0;
  const ranked = league ? rankKeys(keys, S, rec) : [];
  // keyed by the week too: a new week's 0–0 table (cast order) is a reset, never a "move"
  const flash = useRankFlash(C.leaguePending ? [] : ranked, `${C.week}:${div}:${view}`);
  if (!league || !C.season) return <div className="on-note">Loading the League…</div>;
  if (C.leaguePending) return <div className="on-note">A new week — Willie's still settling last week's shift change. The table is back in a moment.</div>;
  const segs = (
    <div className="rt-bar">
      {!race && seg("shatterproof.division", [["circuit", "The Circuit"], ["contenders", "The Contenders"]], circuit ? "circuit" : "contenders", setDiv)}
      {seg("shatterproof.standings", [["week", "This week"], ["season", "Season"], ...(C.v3 ? [["race", "Salvation"]] : [])], race ? "race" : season ? "season" : "week", setView)}
    </div>
  );
  if (race) return <div className="rt">{segs}<Race C={C} limit={40} /></div>;
  const info = hourInfo(hourOf(serverNow()));
  const before0 = S0.size ? rankKeys(keys, S0, rec) : null;
  const pod = ranked.length >= 3 && keys.some((k) => { const [w, l] = rec(statOf(S, k)); return w + l > 0; }) ? ranked.slice(0, 3) : [];   // the top three stand on the podium
  const elos = keys.map((k) => statOf(S, k).elo), eloLo = Math.min(...elos), eloHi = Math.max(...elos);
  const played = keys.some((k) => { const [w, l] = rec(statOf(S, k)); return w + l > 0; });   // zones mean nothing at 0–0
  const lp = apocalypsePlan(league, C.rows, C.week);   // who the Losers' Cup made safe
  const strike = kindHours(C.week, "apocalypse")[0], v2 = hourStart(hoursOfDay(racingDayOf(serverNow())).slice(-1)[0]) >= V2_MS;
  const beforeFri = strike == null ? info.dow !== 6 : hourOf(serverNow()) < strike;   // the table freezes for Apocalypse
  const escaped = new Set((league && league.escaped) || []);
  const zone = (k, i, n) => {
    // a Challenger who hasn't fought enough this week yet: ranked below everyone who has (the table is win %)
    if (isChKey(k) && !season) { const [w, l] = rec(statOf(S, k)); if (w + l < QUALIFY) return { c: "none", t: `${QUALIFY - w - l} to qualify` }; }
    if (!played) return null;
    if (season) return circuit && i < CUP_SIZE ? { c: "top", t: "Finale" } : null;
    if (C.v3) {   // the cup nights: a seat can be lost any night (an escape); this week's escapees can't fall again
      if (circuit && escaped.has(k)) return { c: "rise", t: "escaped" };
      if (circuit) { const live = ranked.filter((x) => !escaped.has(x)); return beforeFri && live.indexOf(k) >= live.length - DROP ? { c: "danger", t: "drop zone" } : null; }
      return i < 8 ? { c: "rise", t: "top half" } : null;
    }
    if (!circuit) return i < DROP && beforeFri ? { c: "rise", t: "promotion" } : null;
    if (lp.safe === k && beforeFri) return { c: "rise", t: "safe" };
    if (i >= n - DROP && beforeFri) return { c: "danger", t: "drop zone" };
    if (v2) return null;   // V2: the Champions Cup seats the week's cup winners first — no fixed line on the table
    return i < CUP_SIZE ? { c: "top", t: "Winners' Cup" } : null;
  };
  const sub = season ? (circuit ? `The season's top ${CUP_SIZE} play the Finale.` : "Every league bout of the season.")
    : C.v3 ? (circuit ? `Win % on Circuit bouts only (${QUALIFY} to qualify). Last place falls whenever a Contender escapes — every night. This week's escapees are safe.` : `Win % on Contenders bouts only (${QUALIFY} to qualify). The top 8 play Last Chance and Top vs Top every night — win one and you escape.`)
    : v2 ? (circuit ? `Ranked by win % (${QUALIFY} fights to qualify). The bottom ${DROP} face Friday's drop.` : `Ranked by win % (${QUALIFY} fights to qualify). The top ${DROP} take on the Circuit's bottom ${DROP} on Friday.`)
      : circuit ? `The top ${CUP_SIZE} play Saturday's Winners' Cup. The bottom ${DROP} face Friday's drop.` : `The top ${DROP} take on the Circuit's bottom ${DROP} on Friday.`;
  return (
    <div className="rt">
      {segs}
      <p className="rt-sub">{C.season.n < 1 ? "Preseason — the table starts Saturday 10pm." : `Season ${C.season.n}, week ${C.season.week} of ${SEASON_WEEKS}. ${sub}`}</p>
      {favs.size > 0 && <div className="rt-favs">{[...favs].map((k) => { const n = nf.get(k), p = placeOf(C, k); return <span key={k} className="being-link" onClick={() => openBeing(k)}><b>★ {whoIs(k).name}</b>{p ? ` #${p.pos}` : ""}{n ? ` · next ${nextTxt(n)}` : ""}</span>; })}</div>}
      {pod.length > 0 && (
        <div className={"pod pod-" + (circuit ? "circuit" : "contenders")}>
          {[1, 0, 2].map((i) => {
            const k = pod[i]; if (!k) return null;
            const s = statOf(S, k), tag = zone(k, i, ranked.length), fl = flash.get(k), n = nf.get(k);
            return (
              <div key={k} className={"pod-c p" + (i + 1) + (favs.has(k) ? " fav" : "") + (fl ? (fl.d > 0 ? " flash up" : " flash down") : "")} onClick={() => openBeing(k)} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") openBeing(k); }}>
                <span className="pod-n">{i + 1}{fl && <em className={fl.d > 0 ? "up" : "down"}>{fl.d > 0 ? "▲" : "▼"}{Math.abs(fl.d)}</em>}</span>
                <button className={"cc-fav" + (favs.has(k) ? " on" : "")} title={favs.has(k) ? "Stop following" : "Follow — starred on every board"} onClick={(e) => { e.stopPropagation(); toggleFav(k); }}>★</button>
                <Trio team={teamNow(C, k)} tier="lord" />
                <b>{whoIs(k).name}</b><ChTag k={k} /><CrownTag C={C} k={k} />
                <span className="pod-rec">{recOf(s, rec)}</span>
                <span className={"sp-form" + (s.streak >= 3 ? " hot" : s.streak <= -3 ? " cold" : "")}>{s.form.map((r, j) => <i key={j} className={r} />)}</span>
                {tag ? <em className={"rt-tag " + tag.c}>{tag.t}</em> : <em className="rt-tag none">&nbsp;</em>}
                <small className="pod-next">{n ? `next ${nextTxt(n)}` : "\u00a0"}</small>
              </div>
            );
          })}
        </div>
      )}
      <table className="rt-table">
        <thead><tr><th className="rt-pos">#</th><th>Aenarchist</th><th>{season ? "Season" : "Week"}</th><th className="rt-form-h">Last 10</th><th className="rt-next">Next</th></tr></thead>
        <tbody>
          {ranked.map((k, i) => {
            if (i < pod.length) return null;   // the podium has them
            const s = statOf(S, k), who = whoIs(k), n = nf.get(k), tag = zone(k, i, ranked.length), mv = moved(k, ranked, before0), fl = flash.get(k);
            const power = eloHi > eloLo ? (s.elo - eloLo) / (eloHi - eloLo) : 0.5;
            return (
              <tr key={k} className={(favs.has(k) ? "fav " : "") + (tag ? "z-" + tag.c + " " : "") + (fl ? (fl.d > 0 ? "flash up" : "flash down") : "")} onClick={() => openBeing(k)}>
                <td className="rt-pos">{i + 1}{fl ? <em className={fl.d > 0 ? "up" : "down"}>{fl.d > 0 ? "▲" : "▼"}{Math.abs(fl.d)}</em>
                  : mv !== 0 && <em className={"day " + (mv > 0 ? "up" : "down")} title={`${mv > 0 ? "up" : "down"} ${Math.abs(mv)} since today began`}>{mv > 0 ? "▲" : "▼"}{Math.abs(mv)}</em>}</td>
                <td><div className="rt-who">
                  <button className={"cc-fav" + (favs.has(k) ? " on" : "")} title={favs.has(k) ? "Stop following" : "Follow — starred on every board"} onClick={(e) => { e.stopPropagation(); toggleFav(k); }}>★</button>
                  <Trio team={teamNow(C, k)} />
                  <div className="rt-name"><b className="being-link">{who.name}</b><ChTag k={k} /><CrownTag C={C} k={k} /><span className="rt-power" title="How strong the ratings say they are"><i style={{ width: `${Math.round(8 + power * 92)}%` }} /></span></div>
                  {tag && <em className={"rt-tag " + tag.c}>{tag.t}</em>}
                </div></td>
                <td className="rt-rec">{recOf(s, rec)}{Math.abs(s.streak) >= 3 && <em className={"rt-hot " + (s.streak > 0 ? "hot" : "cold")}>{streakOf(s)}</em>}</td>
                <td className={"sp-form" + (s.streak >= 3 ? " hot" : s.streak <= -3 ? " cold" : "")} title={`streak ${streakOf(s)}`}>{s.form.map((r, j) => <i key={j} className={r} />)}</td>
                <td className="rt-next">{n ? (n.k !== "lb" || n.fx === "main" ? `${boutLabel(n).replace(/^the /, "")}, ${nextTxt(n)}` : nextTxt(n)) : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {(league.promoted?.length > 0 || league.relegated?.length > 0) && (
        <p className="rt-sub">Last shift change — up: {(league.promoted || []).map((k) => whoIs(k).name).join(", ") || "nobody"}. Down: {(league.relegated || []).map((k) => whoIs(k).name).join(", ") || "nobody"}.</p>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  THE WIRE — Willie's news, from the record (online/wire.js)
// ════════════════════════════════════════════════════════════
const whenOf = (at) => {
  const d = new Date(at), today = new Date(serverNow());
  return d.toDateString() === today.toDateString() ? localTime(at) : d.toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" });
};
/** A headline: {W} {L} become names you can click, {n} the number. */
function Headline({ x }) {
  return <span className="wire-line">{x.line.split(/(\{W\}|\{L\}|\{n\})/).map((part, i) => (
    part === "{W}" || part === "{L}" ? <b key={i} className="being-link" onClick={(e) => { e.stopPropagation(); openBeing(part === "{W}" ? x.W : x.L); }}>{whoIs(part === "{W}" ? x.W : x.L).name}</b>
      : part === "{n}" ? x.n : part
  ))}</span>;
}
function Wire({ items }) {
  if (!items.length) return <div className="on-note">Quiet so far this week. Give it an hour.</div>;
  return (
    <div className="wire">
      {items.map((x) => (
        <div key={x.id} className={"wire-item k-" + x.kind + (CHAMP_KINDS.has(x.kind) && x.kind !== "regular" ? " big" : "")}>
          <span className="wire-at">{whenOf(x.at)}</span><Headline x={x} />
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  THE HALL — every finished season, remembered (hall/{season}, written once)
// ════════════════════════════════════════════════════════════
/**
 * THE HALL OF FAME (caP'n 2026-10-21: "a quick hall of fame that shows winners of the hourlies, of the prime time matches, best
 * fighters of the day, champs of the week etc"). Three views, one card per honour, the rest one tap away:
 *   Tonight — tonight's cup champions, Fighter of the Day, Prime Time's best, the night's last Circuit bout (yesterday's until tonight has any)
 *   This week — every night in a line, then the week's cups, Apocalypse, the tops of the tables, Challenger of the Week
 *   Seasons — every finished season (hall/{season}, written once)
 */
const HALL_VIEWS = [["night", "Tonight"], ["week", "This week"], ["seasons", "Seasons"]];
function Hall({ C }) {
  const [view, setView] = useState(() => { try { return localStorage.getItem("shatterproof.hall") || "night"; } catch { return "night"; } });
  const pickV = (v) => { setView(v); try { localStorage.setItem("shatterproof.hall", v); } catch { /* fine */ } };
  const today = racingDayOf(serverNow());
  const nights = useMemo(() => {
    if (!C.season) return [];
    const out = [];
    for (let d = today; d > today - 7; d--) { const H = dayHonours(C.season.rows, d); if (H.cups.length || H.fighter) out.push(H); }
    return out;
  }, [C.season, today]); // eslint-disable-line react-hooks/exhaustive-deps
  const week = useMemo(() => (C.league ? weekHonours(C.rows, C.league, C.week) : null), [C.rows, C.league, C.week]);
  const who = (k) => (k ? <><b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b><ChTag k={k} /><CrownTag C={C} k={k} /></> : <em>—</em>);
  const whoBare = (k) => <><b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b><ChTag k={k} /></>;   // (a champion line already shows that cup's crown — no week's tag beside it)
  const dayName = (d) => (d === today ? "Tonight" : d === today - 1 ? "Last night" : new Date(hourStart(hoursOfDay(d).slice(-1)[0])).toLocaleDateString([], { weekday: "long" }));
  const tabs = <div className="rt-bar"><div className="seg">{HALL_VIEWS.map(([v, l]) => <button key={v} className={view === v ? "on" : ""} onClick={() => pickV(v)}>{l}</button>)}</div></div>;
  if (view === "seasons") return <div className="hall">{tabs}<HallSeasons C={C} /></div>;
  if (!C.season) return <div className="on-note">Opening the Hall…</div>;
  if (view === "night") {
    const H = nights[0];
    if (!H) return <div className="hall">{tabs}<div className="on-note">Nobody's in the Hall this week yet. The first cups crown their champions tonight.</div></div>;
    return (
      <div className="hall">{tabs}
        <div className="hall-season">
          <div className="hall-h">{dayName(H.day)}</div>
          {H.cups.map((x) => <div key={x.c} className="hall-champ"><Trio team={teamNow(C, x.champion)} tier="lord" />{whoBare(x.champion)}<Crown tier={crownTier(x.c)} size={18} title={x.name.replace(/^the /, "") + " crown"} /><em>{x.name.replace(/^the /, "")} champion</em></div>)}
          <div className="hall-awards">
            {H.fighter && <span>Fighter of the Day {who(H.fighter.key)}<i>{H.fighter.w}–{H.fighter.l}</i></span>}
            {H.prime[0] && <span>Prime Time's best {who(H.prime[0].key)}<i>{H.prime[0].w}–{H.prime[0].l}</i></span>}
            {H.closer && <span>Took the night's last Circuit bout {who(H.closer.key)}<i>over {whoIs(H.closer.opp).name}</i></span>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="hall">{tabs}
      {week && (
        <div className="hall-season">
          <div className="hall-h">Week {C.season.week} of Season {C.season.n}</div>
          {week.cups.filter((x) => ["s", "x", "w", "f"].includes(x.c)).map((x) => <div key={x.c + x.at} className="hall-champ"><Trio team={teamNow(C, x.champion)} tier="lord" />{who(x.champion)}<em>{x.name.replace(/^the /, "")}</em></div>)}
          <div className="hall-awards">
            {week.top.circuit && <span>Top of the Circuit {who(week.top.circuit.key)}<i>{week.top.circuit.w}–{week.top.circuit.l}</i></span>}
            {week.top.contenders && <span>Top Contender {who(week.top.contenders.key)}<i>{week.top.contenders.w}–{week.top.contenders.l}</i></span>}
            {week.challenger && <span>Challenger of the Week {who(week.challenger.key)}<i>{week.challenger.w}–{week.challenger.l}</i></span>}
            {week.cups.filter((x) => x.c === "l").map((x) => <span key={x.at}>Losers' Cup {who(x.champion)}</span>)}
          </div>
          {C.v3 && C.league && (C.league.nights || []).length > 0 && <div className="hall-cups">{C.league.nights.flatMap((n) => n.up.map((k) => <span key={`${n.day}:${k}`}>{who(k)} escaped <i>{new Date(hourStart(hoursOfDay(n.day).slice(-1)[0])).toLocaleDateString([], { weekday: "long" })}</i></span>))}</div>}
          {week.apocalypse.length > 0 && <div className="hall-cups">{week.apocalypse.map((a) => <span key={a.key}>{who(a.key)} {a.rose ? "rose to the Circuit" : "held their seat"} <i>over {whoIs(a.opp).name}</i></span>)}</div>}
        </div>
      )}
      {nights.map((H) => (
        <div key={H.day} className="hall-night">
          <b>{dayName(H.day)}</b>
          {H.cups.filter((x) => ["k", ...NIGHT_ALL].includes(x.c)).map((x) => <span key={x.c}>{CUP_TAG[x.c]} {who(x.champion)}</span>)}
          {H.fighter && <span>Fighter of the Day {who(H.fighter.key)} <i>{H.fighter.w}–{H.fighter.l}</i></span>}
        </div>
      ))}
    </div>
  );
}
function HallSeasons({ C }) {
  const [halls, setHalls] = useState([]);
  const n = C.season ? C.season.n : 0;   // the season on now (1-based); finished ones are 0 … n-2
  useEffect(() => {
    let alive = true;
    Promise.all(Array.from({ length: Math.max(0, n - 1) }, (_, s) => hallOf(net, s).catch(() => null)))
      .then((hs) => { if (alive) setHalls(hs.filter(Boolean).reverse()); });
    return () => { alive = false; };
  }, [n]);
  if (!C.season) return <div className="on-note">Opening the Hall…</div>;
  const fin = finaleAt(Math.max(0, n - 1)), sn = Math.max(1, n);   // preseason (n 0) shows Season 1 waiting
  const who = (k) => <b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b>;
  return (
    <div className="hall">
      <div className="hall-now">Season {sn} {n < 1 ? "hasn't begun yet" : "is being fought"}. Its <b>Finale</b> ends {new Date(fin).toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} — the season's top {CUP_SIZE} of the Circuit. Win it and you're champion.</div>
      {!halls.length && (
        <div className="hall-season ghost">
          <div className="hall-h">Season {sn}</div>
          <div className="hall-champ"><span className="hall-q">?</span><em>Champion — crowned at the Finale</em></div>
          <div className="hall-awards"><span>Top of the Circuit <b>?</b></span><span>Top Contender <b>?</b></span><span>Fastest Ruin <b>?</b></span></div>
        </div>
      )}
      {halls.map((h) => (
        <div key={h.season} className="hall-season">
          <div className="hall-h">Season {h.season + 1}</div>
          <div className="hall-champ">{h.champion ? <><Trio team={whoIs(h.champion).team} tier="lord" />{who(h.champion)}<em>Champion</em></> : <em>The Finale was never fought — no champion this season.</em>}</div>
          <div className="hall-awards">
            {h.top && <span>Top of the Circuit {who(h.top.key)}<i>{h.top.value.join("–")}</i></span>}
            {h.contender && <span>Top Contender {who(h.contender.key)}<i>{h.contender.value.join("–")}</i></span>}
            {h.fastest && <span>Fastest Ruin {who(h.fastest.key)}<i>{mmss(h.fastest.value)} a win</i></span>}
          </div>
          {Array.isArray(h.cups) && h.cups.length > 0 && (
            <div className="hall-cups">{h.cups.map((c, i) => (
              <span key={c.week}>Week {i + 1}: {c.w ? <>{c.week >= weekOf(V3_MS) ? "Salvation" : c.week >= weekOf(V2_MS) ? "Champions Cup" : "Winners' Cup"} {who(c.w)}</> : null}{c.w && c.l ? " · " : ""}{c.l ? <>Losers' Cup {who(c.l)}</> : null}</span>
            ))}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  A BEING'S PAGE — click any name. This season, from the record.
// ════════════════════════════════════════════════════════════
function BeingPage({ k, C, favs, onClose }) {
  useEffect(() => { const esc = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", esc); return () => window.removeEventListener("keydown", esc); }, [onClose]);
  // opponents are weighed by today's ratings (C.S carries them week to week); re-read only when the record moves
  const P = useMemo(() => (C.season ? profileOf(k, C.season.rows, C.S) : null), [k, C.season, C.S]);
  // THE FIGHTER'S CARD (caP'n 2026-10-22: "a baseball card style profile card") — front who they are, back every stat
  const card = useMemo(() => (C.season ? beingCard(k, { seasonRows: C.season.rows, weekRows: C.rows, league: C.league, weekS: C.S, points: C.points, v3: C.v3 }) : null), [k, C.season, C.rows, C.league, C.S, C.points, C.v3]);
  if (!C.season || !C.league) return null;
  const who = whoIs(k), s = statOf(C.season.S, k);
  const vs = (o) => <b className="being-link" onClick={() => openBeing(o)}>{whoIs(o).name}</b>;
  const ch = isChKey(k);
  const M = ch ? null : mindView(C.minds, k), team = M && M.team.length ? M.team : who.team;
  const pts = C.points ? C.points.get(k) || null : null;   // V3: this week's Salvation points
  return (
    <div className="bp-back" onClick={onClose}>
      <div className="bp" onClick={(e) => e.stopPropagation()}>
        <button className="btn ghost tiny bp-x" onClick={onClose}>Close</button>
        {card && <BeingCard card={card} team={team} nameOf={(x) => whoIs(x).name} onOpen={openBeing} fav={favs.has(k)} />}
        <div className="bp-fanrow">
          <button className={"btn tiny " + (favs.has(k) ? "primary" : "ghost")} onClick={() => toggleFav(k)}>{favs.has(k) ? "★ You follow them" : "★ Follow — be a fan"}</button>
        </div>
        {M ? <Mind M={M} /> : <p className="on-note">A Challenger — a player or partner, fighting for real on the League's tables.</p>}
        {pts && <PointsWhy v={pts} C={C} k={k} />}
        {s.played > 0 && <div className="bp-form"><span>Last 10 · streak {streakOf(s)} · avg win {mmss(avgWin(s))}</span><span className="sp-form">{s.form.map((r, j) => <i key={j} className={r} />)}</span></div>}
        {P.fights === 0 ? <p className="on-note">Hasn't fought this season yet.</p> : <>
          <div className="bp-cols">
            <div>
              <h4>Rivals</h4>
              {P.rivals.map((r) => <div key={r.key} className="bp-line">{vs(r.key)}<span>{r.won}–{r.lost} in {r.met}</span></div>)}
              {P.best && <><h4>Biggest win</h4><div className="bp-line">beat {vs(P.best.opp)}<span>{boutLabel(P.best.row)} · {whenOf(P.best.row.at)}</span></div></>}
            </div>
            <div>
              <h4>Recent</h4>
              {P.recent.map(({ row, opp, won }) => <div key={row.id} className="bp-line"><em className={won ? "w" : "l"}>{won ? "W" : "L"}</em>{vs(opp)}<span>{boutLabel(row)}</span></div>)}
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── THEIR POINTS (V3, online/points.js) — one line, the last few wins and what each was worth one tap away ──
function PointsWhy({ v, C, k }) {
  const [open, setOpen] = useState(false);
  const over = C.line != null && v.score >= C.line;
  return (
    <div className="bp-mind">
      <div className="bp-mind-line">
        <span><b>{Math.round(v.cp).toLocaleString()}</b> Circuit points · <b>{Math.round(v.kp).toLocaleString()}</b> Contender points{v.paced ? <> · scored at their pace (×{v.paced})</> : null}</span>
        <em>{C.line == null ? "" : over ? "over the Salvation line" : `${Math.round(C.line - v.score).toLocaleString()} to the line`}</em>
        <button className={"mt-bubble" + (open ? " on" : "")} onClick={() => setOpen(!open)}>Where they came from</button>
      </div>
      {open && (
        <div className="bp-mind-more">
          {v.log.slice().reverse().slice(0, 12).map((x, i) => <div key={i} className="bp-line">+{x.pts} {x.cur === "c" ? "Circuit" : "Contender"}<span>{x.why || "a win"}</span></div>)}
          <p className="on-note">A win pays more for beating someone better, for snapping their streak, for your own run over different opponents, and in a cup. Beating the same one again pays less.</p>
        </div>
      )}
    </div>
  );
}

// ── HOW THEY THINK (online/mind.js) — one line on the page, the rest one tap away ──
function Mind({ M }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bp-mind">
      <div className="bp-mind-line">
        <span><b>{M.temper}</b> · fights as a <b>{M.styleName}</b>{M.loves.length ? <> · loves {M.loves.join(", ")}</> : null}</span>
        <em>{M.mood}</em>
        <button className={"mt-bubble" + (open ? " on" : "")} onClick={() => setOpen(!open)}>How they think</button>
      </div>
      {open && (
        <div className="bp-mind-more">
          <p><b>{M.temper}</b> — {M.temperBlurb}. <b>{M.styleName}</b> — {M.styleBlurb}.</p>
          {M.changes.length ? M.changes.map((c, i) => <div key={i} className="bp-line">{c.text}<span>{c.why} · {whenOf(hourStart(c.h))}</span></div>)
            : <p className="on-note">Hasn't changed a thing yet.</p>}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  THE META — what the League is fighting with (caP'n: "important to show stats on
//  what's being used, its effectiveness … but never ever want to drown the player").
//  One sentence and one strip up top; everything else behind a bubble.
// ════════════════════════════════════════════════════════════
const META_MORE = [["styles", "How they fight"], ["beings", "Beings"], ["trios", "Trios"], ["moves", "Who's changing"]];
function Meta({ C }) {
  const [view, setView] = useState("week");
  const [more, setMore] = useState(null);
  const B = useMemo(() => (C.season ? metaBoard(view === "season" ? C.season.rows : C.rows) : null), [C.season, C.rows, view]);
  if (!B) return <div className="on-note">Loading the League…</div>;
  const views = <div className="rt-views">{[["week", "This week"], ["season", "Season"]].map(([v, l]) => <button key={v} className={"cc-tab" + (view === v ? " on" : "")} onClick={() => setView(v)}>{l}</button>)}</div>;
  if (!B.fights) return <div className="mt">{views}<div className="on-note">No fights to read yet {view === "season" ? "this season" : "this week"}. Give the League an hour.</div></div>;
  const top = B.styles[0];
  const played = B.styles.filter((s) => s.uses >= Math.max(3, B.fights * 0.05));
  const best = played.slice().sort((a, b) => (b.win || 0) - (a.win || 0))[0];
  const who = (k) => <b className="being-link" onClick={() => openBeing(k)}>{whoIs(k).name}</b>;
  return (
    <div className="mt">
      <div className="rt-bar">{views}</div>
      <p className="mt-head">
        <b>{top.name}s</b> are everywhere — {pct(top.share)} of fights.
        {best && best.id !== top.id ? <> But <b>{best.name}s</b> are winning the most ({pct(best.win)}).</> : <> And they're winning {pct(top.win)}.</>}
      </p>
      <div className="mt-strip">
        {B.styles.map((s) => <span key={s.id} title={`${STYLES[s.id].blurb} — ${s.uses} fights, ${pct(s.win)} won`}><i style={{ width: pct(s.share) }} />{s.name}<em>{pct(s.share)}</em></span>)}
      </div>
      <div className="mt-bubbles">{META_MORE.map(([k, l]) => <button key={k} className={"mt-bubble" + (more === k ? " on" : "")} onClick={() => setMore(more === k ? null : k)}>{l}</button>)}</div>
      {more === "styles" && <div className="mt-more">{B.styles.map((s) => <div key={s.id} className="bp-line"><b>{s.name}</b> <i className="mt-blurb">{STYLES[s.id].blurb}</i><span>{pct(s.share)} of fights · wins {s.win == null ? "—" : pct(s.win)}</span></div>)}</div>}
      {more === "beings" && <div className="mt-more">{B.beings.map((x) => <div key={x.line} className="bp-line"><Trio team={[x.line]} />{x.name}<span>on {pct(x.share)} of teams · wins {x.win == null ? "—" : pct(x.win)}</span></div>)}</div>}
      {more === "trios" && <div className="mt-more">{B.trios.map((x) => <div key={x.t.join()} className="bp-line"><Trio team={x.t} />{x.t.map(nameOfId).join(" · ")}<span>{x.uses} fights · wins {pct(x.win)}</span></div>)}</div>}
      {more === "moves" && <div className="mt-more">{B.changes.length ? B.changes.slice(0, 20).map((c) => <div key={`${c.key}:${c.h}`} className="bp-line">{who(c.key)}<span>{c.why} · {whenOf(hourStart(c.h))}</span></div>) : <p className="on-note">Nobody's changed a thing yet.</p>}</div>}
      <p className="mt-foot">From {B.fights} fights the League really fought. Fights decided on paper don't count here — nobody saw how they fought.</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  THE WEEK
// ════════════════════════════════════════════════════════════
const HEADLINER = { main: "The Main Event", losers: "The Losers' Cup", apocalypse: "Apocalypse", winners: "The Winners' Cup", finale: "The Season Finale", kcup: "Two cups", ccup: "Two cups", champions: "The Champions Cup", salvation: "Salvation", rounds: "The cup night" };
function Schedule() {
  const [open, setOpen] = useState(false);
  const now = serverNow(), today = racingDayOf(now);
  const days = Array.from({ length: 7 }, (_, i) => today + i);   // the next seven nights, starting tonight
  const v2 = now >= V2_MS - 7 * 864e5;   // the new evenings are what's coming
  const v3 = now >= V3_MS - 7 * 864e5;   // the cup nights are what's coming
  return (
    <div className="sch">
      <div className="sch-week">
        {days.map((d) => {
          const hs = hoursOfDay(d), cups = cupsOfDay(d).filter((c) => !QUALS.includes(c));   // (the Qualifiers are every day — the title is the day's headline)
          // V2 nights: the cups (Saturday's headliner is its last cup — the Champions Cup or the Finale)
          const head = cups.find((c) => c === "x" || c === "f" || c === "s") || cups[cups.length - 1];   // Saturday's headliner is the Champions Cup / Salvation (or the Finale), not the Losers' Cup
          const night3 = cups.includes("bb");   // V3: the cup night — five cups, finals 6–10pm
          const first = night3 ? cupHours("bb", d).find((h) => hourInfo(h).localHour === 18) : cups.length ? cupHours(head, d)[0] : hs.find((h) => HEADLINER[hourInfo(h).kind]);
          const info = first != null ? hourInfo(first) : null;
          const title = night3 ? "The cup night" : cups.length === 2 && cups.includes("k") ? "Contenders + Circuit Cups" : info ? HEADLINER[info.kind] : "League fights";
          const fin = cups.length ? cupHours(cups.includes("c") ? "c" : cups[0], d).slice(-1)[0] : null;
          return (
            <div key={d} className={"sch-day" + (d === today ? " now" : "") + (info ? " k-" + info.kind : "")}>
              <span className="sch-dow">{d === today ? "Tonight" : new Date(hourStart(hs[hs.length - 1])).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</span>
              <b>{hourStart(hs[hs.length - 1]) < GENESIS_MS ? "Preseason" : title}</b>
              <small>{night3 ? `finals ${localTime(hourStart(first))}–${localTime(hourStart(fin))}` : fin != null ? `final ${localTime(hourStart(fin))}` : info ? localTime(hourStart(first)) : ""}{d === today && info && hourStart(first) > now ? ` · in ${untilTxt(hourStart(first) - now)}` : ""}</small>
            </div>
          );
        })}
      </div>
      <p className="rt-sub">{v3 ? "All day: two stages side by side — the Circuit's and the Contenders' (a fight at least every 5 minutes). Neighbours on the table fight each other, and every league bout moves it."
        : "All day: one fight at a time, back to back — neighbours on the table fight each other, and every league bout moves it."}</p>
      <button className={"mt-bubble" + (open ? " on" : "")} onClick={() => setOpen(!open)}>How the League works</button>
      {open && (v3 ? (
        <div className="wk-rules">
          <p><b>All day</b> — two stages at once. The Circuit stage: Circuit bouts, called at 5:00. The Contenders stage: Contenders bouts, called at 4:00, a fight at least every 5 minutes. Each table is win % on its own bouts ({QUALIFY} to qualify).</p>
          <p><b>Every day — the Qualifiers.</b> From 11am (Friday and Saturday too), four baby tournaments of 8 fighters run back to back — two on the Circuit stage, two on the Contenders'. Win one and you take a seat in that night's cups, and wear a purple-black-flame <b>Q</b> beside your name all week. On Friday and Saturday they help decide the weekend's seats.</p>
          <p><b>Sunday to Thursday — the cup night.</b> Five cups, drawn off one table at 1pm, early rounds through the afternoon, then one final an hour: <b>6pm Bottom vs Bottom</b> (each division's bottom 8) · <b>7pm the Contenders Cup</b> (all 16) · <b>8pm Last Chance</b> (the Contenders' top 8 vs the Circuit's bottom 8) · <b>9pm Top vs Top</b> (the top 8 of each) · <b>10pm the Circuit Cup</b> (all 16) — then the <b>Night Crown</b>, one more fight between the night's best champions.</p>
          <p><b>Escapes</b> — a Contender who wins Last Chance or Top vs Top escapes to the Circuit when the night's cups are over; the Circuit's last place falls to the Contenders. This week's escapees can't fall again this week.</p>
          <p><b>Friday</b> — Apocalypse at 8, 9 and 10pm: the Circuit's bottom {DROP} against the Contenders' top {DROP}, best of 3. Win the series, take the seat.</p>
          <p><b>Saturday</b> — the <b>Losers' Cup</b> from 6pm (bragging rights) and <b>Salvation</b>, the King's Cup, from 6pm, final at 10pm: everyone whose points are over the line (the Circuit's middle score that week), best first, sixteen at most. Then the week starts over.</p>
          <p><b>Points</b> — every win pays, in the division of who you beat (a Circuit win is worth far more). More for beating someone better, snapping their streak, your own run over different opponents, a cup. Less for beating the same one again.</p>
          <p><b>Every {SEASON_WEEKS}th Saturday</b> — a whole day of big fights: the Season Finale (the season's top {CUP_SIZE} of the Circuit) from 2pm, Salvation and the Losers' Cup from 6pm, and the Finale's final closes the season at 10pm. Win it and you're the champion, in the Hall forever.</p>
          <p><b>Players and partners</b> — join once and you're a Challenger, starting in the Contenders. Switch "I'm in" on to be on the cards. Check in once for the whole night — the Qualifiers and the cups (any time up to 10:49am), or for Saturday's cups (5:40–5:50pm). Fight fewer bouts than the NPCs and your points are scored at your pace.</p>
        </div>
      ) : v2 ? (
        <div className="wk-rules">
          <p><b>All day</b> — league fights, one at a time. The table is win % ({QUALIFY} fights to qualify). 6pm to 10pm is Prime Time: the top of the Circuit fights first.</p>
          <p><b>Sunday to Thursday</b> — two cups a night, both from 6pm. The <b>Circuit Cup</b> (all 16) — final at 10pm. The <b>Contenders Cup</b> (the Contenders' top 8) — final at 9pm.</p>
          <p><b>Friday</b> — Apocalypse at 8, 9 and 10pm: the Circuit's bottom {DROP} against the Contenders' top {DROP}, best of 3. Win the series, take the seat.</p>
          <p><b>Saturday</b> — the <b>Losers' Cup</b> from 6pm (bragging rights, open to every player not in the Champions Cup) and the <b>Champions Cup</b> from 8pm, final at 10pm: the week's cup winners first, the top of the table fills it. Then the week's table starts over.</p>
          <p><b>Every {SEASON_WEEKS}th Saturday</b> — the Season Finale instead of the Champions Cup: the season's top {CUP_SIZE}. Win it and you're the champion, in the Hall forever.</p>
          <p><b>Players and partners</b> — join once and you're a Challenger, on the Contenders' table. Switch "I'm in" on to be on the cards; check in during the 10 minutes before a cup's card goes up to enter it.</p>
        </div>
      ) : (
        <div className="wk-rules">
          <p><b>Every night</b> — 5pm to 8pm is Prime Time: the top of the Circuit fights first. At 9pm, the Main Event: the Circuit's #1 vs #2.</p>
          <p><b>Thursday</b> — the Losers' Cup: the Circuit's bottom {CUP_SIZE}. Win it and you're safe from Friday's drop.</p>
          <p><b>Friday</b> — Apocalypse: the Circuit's bottom {DROP} against the Contenders' top {DROP}, one-on-one, best of 3. Win the series, take the seat.</p>
          <p><b>Saturday</b> — the Winners' Cup: the Circuit's top {CUP_SIZE}, final at 9pm. Then the week's table starts over.</p>
          <p><b>Every {SEASON_WEEKS}th Saturday</b> — the Season Finale instead: the season's top {CUP_SIZE}. Win it and you're the champion, in the Hall forever.</p>
        </div>
      ))}
    </div>
  );
}

// the Wire, one headline at a time — turns over by itself, tap it to open the Wire
function WireTicker({ items, onOpen }) {
  const [n, setN] = useState(0);
  const m = Math.min(items.length, 4);
  useEffect(() => { if (m < 2) return undefined; const id = setInterval(() => setN((x) => x + 1), 6500); return () => clearInterval(id); }, [m]);
  const x = items[m ? n % m : 0];
  if (!x) return <div className="oa-wire" />;
  return <button className="oa-wire" onClick={onOpen} title="The Wire"><b>The Wire</b><span key={x.id} className="oa-wire-in"><Headline x={x} /></span></button>;
}
// a slow sky behind the whole League: two drifting lights and a few embers
const EMBERS = Array.from({ length: 16 }, (_, i) => ({ x: `${(i * 37 + 11) % 100}%`, s: `${2 + (i % 3)}px`, d: `${14 + (i * 7) % 12}s`, w: `${-(i * 3.1)}s`, dx: `${((i % 5) - 2) * 40}px`, c: i % 3 === 0 ? "#7CE7FF" : i % 3 === 1 ? "#F2C14E" : "#FF4D8D" }));
const Sky = () => <div className="lg-sky" aria-hidden>{EMBERS.map((e, i) => <i key={i} style={{ "--x": e.x, "--s": e.s, "--d": e.d, "--w": e.w, "--dx": e.dx, "--c": e.c }} />)}</div>;

// ════════════════════════════════════════════════════════════
//  THE SCREEN
// ════════════════════════════════════════════════════════════
const TABS = [["stage", "On now"], ["bracket", "Brackets"], ["ratings", "Standings"], ["wire", "The Wire"], ["meta", "The Meta"], ["hall", "The Hall"], ["week", "Schedule"]];
export function HourlyScreen({ on, onWatch, onRingside, onBack, preview = null, team = null, tomes = null, onPickTeam = null, toast = null }) {   // preview: dev-only made-up week (leaguePreview.jsx)
  useTick(1000);
  const [tab, setTab] = useState(() => { try { const t = (preview && new URLSearchParams(window.location.search).get("tab")) || localStorage.getItem("shatterproof.tab"); return TABS.some(([k]) => k === t) ? t : "stage"; } catch { return "stage"; } });
  const pick = (t) => { setTab(t); try { localStorage.setItem("shatterproof.tab", t); } catch { /* fine */ } };
  const [st, setSt] = useState(preview ? preview.st : null);
  const live = useCircuit(!!on.authUid && !preview);
  const C = preview ? preview.C : live;
  const favs = useFavs();
  const openKey = useOpenBeing();
  useEffect(() => closeBeing, []);   // leaving the League closes any open page
  // THE WIRE — this week's storylines, re-read whenever the record moves
  const wire = useMemo(() => (C.league ? wireOf(C.rows, { league: C.league, eloStart: C.league.elo, nameOf: nameOfId, start: C.league.minds && C.league.minds.n }) : []), [C.rows, C.league]);
  const ticker = wire[0];
  const [seen, setSeen] = useState(() => { try { return localStorage.getItem("shatterproof.wireSeen") || ""; } catch { return ""; } });
  useEffect(() => { if (tab === "wire" && ticker && ticker.id !== seen) { setSeen(ticker.id); try { localStorage.setItem("shatterproof.wireSeen", ticker.id); } catch { /* fine */ } } }, [tab, ticker, seen]);
  const hourNow = hourOf(serverNow());
  useEffect(() => {
    if (!on.authUid || preview) return undefined;   // the database answers signed-in souls only
    let alive = true, running = false;
    const load = async () => {
      if (running) return;               // a slow look (a replay) never piles up behind itself
      running = true;
      try {
        const now = serverNow();
        const s = await loadStage(net, now).catch((e) => { console.warn(e); return null; }); // eslint-disable-line no-console
        // a finished bout goes in the record straight away (the keeper would get there too)
        if (s) for (const H of [s.prev, s.cur]) if (H) for (const f of H.bouts) if (f.phase === "done") record(net, f, H.info).catch(() => {});
        if (alive && s) setSt(s);
      } finally { running = false; }
    };
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [on.authUid, hourNow]);
  if (!on.authUid) return (
    <div className="screen online"><div className="panel on-panel"><h2>The League</h2><p className="on-note">Sign in to see the League — the Empire only talks to signed-in souls.</p>
      <div className="title-buttons"><button className="btn ghost" onClick={onBack}>Back</button></div></div></div>
  );
  const now = serverNow();
  const s = stageStatus(st, now);
  const info = st && st.cur ? st.cur.info : null;
  return (
    <div className="screen online hourly">
      <Sky />
      <div className="on-top"><button className="btn ghost" onClick={onBack}>← Back</button><h2>The League</h2><span /></div>
      <div className={"oa" + (tab === "stage" ? " slim" : "") + (s.live ? " live" : "") + (info ? " " + (KIND_CLASS[info.kind] || "") : "")}>
        {info && <div className="oa-kind">{st.cur.note || info.name}</div>}
        {tab !== "stage" && <div className="oa-line">{s.live && <i className="fo-dot" />}<span>{s.line}</span>{s.at != null && <b className="oa-clock">{clock(s.at - now)}</b>}</div>}
        {C.behind > 0 ? <div className="oa-wire">{C.behind} hour{C.behind === 1 ? "" : "s"} of fights to catch up on…</div>
          : <WireTicker items={wire} onOpen={() => pick("wire")} />}
      </div>
      <div className="cc-tabs">{TABS.map(([k, label]) => <button key={k} className={"cc-tab" + (tab === k ? " on" : "")} onClick={() => pick(k)}>{label}{k === "wire" && ticker && ticker.id !== seen && tab !== "wire" && <i className="pip" title="New on the Wire" />}</button>)}</div>

      <div className="lg-page" key={tab}>
      {tab === "stage" && !preview && <ChallengerStrip st={st} C={C} on={on} team={team} tomes={tomes} onPickTeam={onPickTeam} onRingside={onRingside} toast={toast} />}
      {tab === "stage" && <Stage st={st} C={C} on={on} onWatch={onWatch} onRingside={onRingside} favs={favs} go={pick} />}
      {tab === "bracket" && <Bracket C={C} st={st} onJump={pick} />}
      {tab === "ratings" && <Standings C={C} st={st} favs={favs} />}
      {tab === "meta" && <Meta C={C} />}
      {tab === "wire" && <Wire items={wire} />}
      {tab === "hall" && <Hall C={C} />}
      {tab === "week" && <Schedule />}
      </div>
      {openKey && <BeingPage k={openKey} C={C} favs={favs} onClose={closeBeing} />}
    </div>
  );
}
